import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import LsdBar, { lsdFromComponents } from '../LsdBar';
import useCsmCatalogue from '../../hooks/useCsmCatalogue';
import { parseLsd, siteComponents, siteError } from '../../utils/siteLocation';

export const PROVINCES = ['Alberta', 'Saskatchewan', 'British Columbia', 'Manitoba', 'Other'];

export const emptySite = () => ({ id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())), name: '', lsd: '', province: '', address: '', components: {} });

// Site = name + province + structured LSD (AB/SK/BC) or free-text LSD + address.
export default function SiteFields({ value, onChange, compact = false, testPrefix = 'site', dark = false, nameReadOnly = false }) {
  const csm = useCsmCatalogue();
  const layout = csm.lsdLayout(value.province);
  const components = siteComponents(value, layout);
  const error = siteError(value, layout);
  const hasLsd = Boolean((value.lsd || '').trim() || Object.values(components).some((v) => String(v || '').trim()));
  const set = (patch) => onChange({ ...value, components, ...patch });
  const setProvince = (province) => {
    const nextLayout = csm.lsdLayout(province);
    const next = nextLayout?.name === layout?.name ? components : parseLsd(nextLayout, value.lsd);
    set({ province: province === 'none' ? '' : province, components: nextLayout ? next : {}, lsd: nextLayout ? lsdFromComponents(nextLayout, next) : value.lsd });
  };
  const h = compact ? 'h-8 text-xs' : '';
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-fields`}>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px] gap-2">
        <Input aria-label="Site name" readOnly={nameReadOnly} value={value.name || ''} onChange={(e) => !nameReadOnly && set({ name: e.target.value })} placeholder="Site name (e.g. Red Deer Battery)" className={h} data-testid={`${testPrefix}-name`} />
        <Select value={value.province || 'none'} onValueChange={setProvince}>
          <SelectTrigger className={h} aria-label="Province (optional)" data-testid={`${testPrefix}-province`}><SelectValue placeholder="Province (optional)" /></SelectTrigger>
          <SelectContent className={`z-[140] ${dark ? 'dark' : ''}`} data-testid={`${testPrefix}-province-options`}>
            <SelectItem value="none" data-testid={`${testPrefix}-province-none`}>Province (optional)</SelectItem>
            {PROVINCES.map((p) => <SelectItem key={p} value={p} data-testid={`${testPrefix}-province-${p.toLowerCase().replace(/\s+/g, '-')}`}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between min-h-6">
        <span className="text-xs text-slate-500" data-testid={`${testPrefix}-lsd-label`}>LSD (optional)</span>
        {hasLsd && <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" title="Clear LSD" aria-label="Clear LSD"
          onClick={() => set({ lsd: '', components: {} })} data-testid={`${testPrefix}-clear-lsd`}><X size={13} /></Button>}
      </div>
      {layout ? (
        <div>
          <LsdBar layout={layout} values={components} testid={`${testPrefix}-lsd`}
            onChange={(components) => set({ components, lsd: lsdFromComponents(layout, components) })} />
          <p className="text-[11px] text-slate-500 mt-1 font-mono break-all" data-testid={`${testPrefix}-lsd-preview`}>LSD: {value.lsd || '—'}</p>
          {error && <p role="alert" className="text-xs text-red-600 mt-1" data-testid={`${testPrefix}-lsd-error`}>{error}</p>}
        </div>
      ) : (
        <Input aria-label="LSD / legal location (optional)" value={value.lsd || ''} onChange={(e) => set({ lsd: e.target.value })} placeholder="LSD / legal location (optional)" className={`${h} font-mono`} data-testid={`${testPrefix}-lsd-text`} />
      )}
      <Input aria-label="Street address / directions" value={value.address || ''} onChange={(e) => set({ address: e.target.value })} placeholder="Street address / directions (optional)" className={h} data-testid={`${testPrefix}-address`} />
    </div>
  );
}
